export function buildCartItems(variants, names) {
  if (!Array.isArray(names) || names.length < 1 || names.length > 3) throw new Error('Bitte wähle ein gültiges Set.');
  const grouped = new Map();
  for (const name of names) {
    const variant = variants.find(item => item.name === name);
    if (!variant || !Number.isSafeInteger(variant.id) || variant.id <= 0) throw new Error('Bitte wähle jeden Tierfreund erneut aus.');
    const item = grouped.get(variant.id);
    if (item) item.quantity += 1;
    else grouped.set(variant.id, { id: variant.id, quantity: 1 });
  }
  return [...grouped.values()];
}

export function selectionSummary(variants, names, discounts = [0, 990, 1980]) {
  const items = buildCartItems(variants, names);
  const selected = names.map(name => variants.find(variant => variant.name === name));
  const subtotal = selected.reduce((sum, variant) => sum + variant.price, 0);
  const discount = discounts[names.length - 1];
  if (!Number.isSafeInteger(discount) || discount < 0 || selected.some(v => !Number.isSafeInteger(v.price) || v.price < 0)) throw new Error('Die Preise konnten nicht geladen werden.');
  return { items, subtotal, discount, total: Math.max(0, subtotal - discount), available: selected.every(v => v.available), firstVariant: selected[0] };
}

export async function addCartItems(url, items, fetcher = globalThis.fetch) {
  let response;
  try {
    response = await fetcher(url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
  } catch {
    const error = new Error('Die Verbindung wurde unterbrochen. Bitte prüfe zuerst deinen Warenkorb, bevor du die Auswahl erneut hinzufügst.');
    error.uncertain = true;
    throw error;
  }
  let result;
  try { result = await response.json(); } catch {
    const error = new Error('Der Warenkorb konnte nicht bestätigt werden. Bitte prüfe deinen Warenkorb, bevor du es erneut versuchst.');
    error.uncertain = true;
    throw error;
  }
  if (!response.ok) {
    const error = new Error(typeof result.description === 'string' ? result.description : 'Diese Auswahl konnte nicht hinzugefügt werden. Bitte prüfe die Verfügbarkeit und versuche es erneut.');
    error.status = response.status;
    throw error;
  }
  return result;
}

if (typeof customElements !== 'undefined' && !customElements.get('ks-light-buybox')) {
  class LightBuybox extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      try { this.data = JSON.parse(this.querySelector('[data-lb-config]').textContent); }
      catch { this.querySelector('[data-lb-status]').textContent = 'Die Auswahl konnte nicht geladen werden. Bitte lade die Seite neu.'; return; }
      this.qty = 1;
      this.picks = [0, 1, 2].map(i => Math.min(i, this.data.animals.length - 1));
      this.index = 0;
      this.busy = false;
      this.format = new Intl.NumberFormat(this.data.locale || 'de-DE', { style: 'currency', currency: this.data.currency || 'EUR' });
      // Shopify's product.images defines both the gallery contents and their exact order.
      this.images = (this.data.gallery || []).map((image, i) => ({ ...image, key: `product-${i}`, placeholder: false }));
      this.animalGalleryIndices = this.data.animals.map(animal => {
        const variant = this.data.variants.find(item => item.name === animal.name);
        const index = this.images.findIndex(image => variant?.imageId != null && String(image.id) === String(variant.imageId));
        return index >= 0 ? index : 0;
      });
      this.form = this.querySelector('[data-lb-form]');
      const requested = new URLSearchParams(location.search).get('variant');
      const variant = this.data.variants.find(v => String(v.id) === requested);
      if (variant) this.selectVariant(variant);
      this.form.addEventListener('submit', event => { event.preventDefault(); this.addToCart(); }, { signal: this.abort.signal });
      this.addEventListener('change', event => {
        if (this.busy) return;
        if (event.target.matches('[data-lb-tier]')) { this.qty = Number(event.target.value); this.renderExtras(); this.update(true); }
        if (event.target.matches('[data-lb-pick]')) {
          const slot = Number(event.target.dataset.lbPick);
          this.picks[slot] = Number(event.target.value);
          if (!slot) this.show(this.animalGalleryIndices[this.picks[0]]);
          this.update(true);
        }
      }, { signal: this.abort.signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.matches('[data-lb-thumb]')) this.show(Number(button.dataset.lbThumb));
        if (button.matches('[data-lb-prev]')) this.show(this.index - 1);
        if (button.matches('[data-lb-next]')) this.show(this.index + 1);
        if (button.matches('[data-lb-sticky-atc]')) this.form.requestSubmit();
      }, { signal: this.abort.signal });
      let touch;
      const stage = this.querySelector('[data-lb-stage]');
      stage.addEventListener('touchstart', event => { touch = event.changedTouches[0]; }, { passive: true, signal: this.abort.signal });
      stage.addEventListener('touchend', event => {
        if (!touch) return;
        const end = event.changedTouches[0], dx = end.clientX - touch.clientX, dy = end.clientY - touch.clientY;
        if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy)) this.show(this.index + (dx < 0 ? 1 : -1));
        touch = null;
      }, { passive: true, signal: this.abort.signal });
      this.sticky = this.querySelector('[data-lb-sticky]');
      this.observer = new IntersectionObserver(([entry]) => { this.sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0; });
      this.observer.observe(this.querySelector('[data-lb-main-atc]'));
      this.renderExtras();
      this.show(variant ? this.animalGalleryIndices[this.picks[0]] : 0);
      this.update();
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.ready = false; }
    money(cents) { return this.format.format(cents / 100); }
    selectedNames(quantity = this.qty) { return this.picks.slice(0, quantity).map(i => this.data.animals[i].name); }
    selectVariant(variant) {
      const pick = this.data.animals.findIndex(animal => animal.name === variant.name);
      if (pick < 0) return;
      this.picks[0] = pick;
      this.querySelector(`[data-lb-pick="0"][value="${pick}"]`).checked = true;
      this.show(this.animalGalleryIndices[pick]);
    }
    renderExtras() {
      const root = this.querySelector('[data-lb-extras]');
      root.replaceChildren();
      for (let slot = 1; slot < this.qty; slot++) {
        const field = document.createElement('fieldset'); field.className = 'lb-fieldset';
        const legend = document.createElement('legend'); legend.className = 'ks-lbl'; legend.textContent = `${this.data.labels.slot} ${slot + 1}`;
        const grid = document.createElement('div'); grid.className = 'ks-chars';
        this.data.animals.forEach((animal, i) => {
          const label = document.createElement('label'); label.className = 'ks-char lb-option';
          const radio = document.createElement('input'); radio.type = 'radio'; radio.name = `lb-animal-${this.data.sectionId}-${slot}`; radio.value = i; radio.dataset.lbPick = slot; radio.checked = this.picks[slot] === i;
          const picture = document.createElement('span'); picture.className = 'ks-char__img';
          const image = document.createElement('img'); image.src = animal.image; image.alt = ''; image.width = 160; image.height = 160; image.loading = 'lazy'; picture.append(image);
          const name = document.createElement('span'); name.className = 'ks-char__n'; name.textContent = animal.name;
          label.append(radio, picture, name); grid.append(label);
        });
        field.append(legend, grid); root.append(field);
      }
    }
    update(updateUrl = false) {
      const names = this.selectedNames();
      try { this.selection = selectionSummary(this.data.variants, names, this.data.setDiscounts); }
      catch { this.selection = null; }
      const price = this.selection?.total ?? this.data.prices[this.qty - 1];
      const inputs = this.querySelector('[data-lb-line-items]'); inputs.replaceChildren();
      (this.selection?.items || []).forEach((item, i) => {
        for (const key of ['id', 'quantity']) { const input = document.createElement('input'); input.type = 'hidden'; input.name = `items[${i}][${key}]`; input.value = item[key]; inputs.append(input); }
      });
      for (const selector of ['[data-lb-total]', '[data-lb-heading-price]', '[data-lb-sticky-price]']) this.querySelector(selector).textContent = this.money(price);
      const compareEl = this.querySelector('[data-lb-compare-price]');
      const discountEl = this.querySelector('[data-lb-discount-badge]');
      if (compareEl && discountEl) {
        const compareTotal = names.reduce((sum, name) => {
          const variant = this.data.variants.find(item => item.name === name);
          return variant ? sum + Math.max(variant.price, variant.compareAtPrice || variant.price) : sum;
        }, 0);
        const isSale = Boolean(this.selection) && compareTotal > price;
        compareEl.hidden = !isSale;
        discountEl.hidden = !isSale;
        if (isSale) {
          compareEl.textContent = this.money(compareTotal);
          discountEl.textContent = '−' + Math.round((compareTotal - price) * 100 / compareTotal) + '%';
        }
      }
      this.querySelector('[data-lb-sticky-name]').textContent = names.join(' + ');
      this.querySelector('[data-lb-sticky-img]').src = this.data.animals[this.picks[0]].image;
      this.querySelectorAll('[data-lb-tier-price]').forEach(node => {
        const quantity = Number(node.dataset.lbTierPrice);
        let tierPrice = this.data.prices[quantity - 1];
        let comparison = this.data.prices[0] * quantity;
        try {
          const quote = selectionSummary(this.data.variants, this.selectedNames(quantity), this.data.setDiscounts);
          tierPrice = quote.total;
          comparison = quote.subtotal;
          if (quantity === 1) {
            const original = quote.firstVariant.compareAtPrice || quote.firstVariant.price;
            comparison = Math.max(quote.firstVariant.price, original);
          }
        } catch {}
        node.textContent = this.money(tierPrice);
        const saved = Math.max(0, comparison - tierPrice);
        const offer = this.querySelector(`[data-lb-tier-offer="${quantity}"]`);
        const compare = this.querySelector(`[data-lb-tier-compare="${quantity}"]`);
        if (offer) {
          offer.hidden = saved === 0;
          if (saved > 0) offer.textContent = quantity === 1
            ? `−${Math.round(saved * 100 / comparison)}% Rabatt`
            : `Zusätzlich ${this.money(saved)} sparen`;
        }
        if (compare) {
          compare.hidden = saved === 0;
          if (saved > 0) compare.textContent = this.money(comparison);
        }
      });
      this.querySelector('[data-lb-saving]').textContent = this.qty > 1 && this.selection?.discount > 0 ? `${this.data.labels.saving} ${this.money(this.selection.discount)}` : '';
      const enabled = this.data.purchasable && Boolean(this.selection?.available) && !this.busy;
      const text = this.busy ? this.data.labels.adding : !this.data.purchasable ? this.data.labels.draft : this.selection?.available ? this.data.labels.add : this.data.labels.soldOut;
      this.querySelectorAll('[data-lb-atc]').forEach(button => { button.disabled = !enabled; button.textContent = text; button.setAttribute('aria-busy', String(this.busy)); });
      this.querySelector('[data-lb-status]').textContent = !this.selection ? this.data.labels.invalid : !this.data.purchasable ? this.data.labels.draftNote : !this.selection.available ? this.data.labels.soldOutNote : '';
      if (updateUrl && this.selection && this.data.purchasable) { const url = new URL(location.href); url.searchParams.set('variant', this.selection.firstVariant.id); history.replaceState({}, '', url); }
    }
    show(index) {
      this.index = (index + this.images.length) % this.images.length;
      const image = this.images[this.index], main = this.querySelector('[data-lb-main]');
      main.src = image.src; main.alt = image.alt || this.data.labels.effect;
      this.querySelector('[data-lb-stage]').classList.toggle('has-product-photo', !image.placeholder);
      this.querySelectorAll('[data-lb-thumb]').forEach(button => button.setAttribute('aria-current', String(Number(button.dataset.lbThumb) === this.index)));
    }
    async addToCart() {
      if (this.busy || !this.data.purchasable || !this.selection?.available) return;
      const items = buildCartItems(this.data.variants, this.selectedNames());
      this.busy = true; this.update();
      this.querySelectorAll('input[type="radio"]').forEach(input => { input.disabled = true; });
      const errorNode = this.querySelector('[data-lb-error]'); errorNode.hidden = true;
      try { await addCartItems(this.data.cartAddUrl, items); location.assign(this.data.cartUrl); }
      catch (error) {
        this.busy = false;
        this.querySelectorAll('input[type="radio"]').forEach(input => { input.disabled = false; });
        this.update(); errorNode.textContent = error.message; errorNode.hidden = false; errorNode.focus(); this.querySelector('[data-lb-cart-link]').hidden = false;
      }
    }
  }
  customElements.define('ks-light-buybox', LightBuybox);
}
